import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, Star, MapPin } from "lucide-react";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { CONTACT, IMAGES } from "../../data/content";

const LINES = [
  "Fontanero en Huelva",
  "con 20 años de experiencia.",
  "Contáctenos sin compromiso.",
];

const lineParent = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};
const lineChild = {
  hidden: { y: "110%" },
  visible: { y: "0%", transition: { duration: 0.9, ease: [0.76, 0, 0.24, 1] } },
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.05]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative pt-28 md:pt-40 pb-16 md:pb-24 overflow-hidden">
      <div className="pointer-events-none absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full bg-[#5AB82D]/10 blur-3xl" />
      <div className="max-w-[1400px] mx-auto px-5 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="inline-flex items-center gap-2 rounded-full bg-[#EBF6E4] border border-[#5AB82D]/20 px-4 py-1.5 mb-7"
              data-testid="hero-rating-badge"
            >
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3.5 h-3.5 fill-[#5AB82D] text-[#5AB82D]" />
                ))}
              </div>
              <span className="text-xs font-bold text-[#2E5A1B]">5.0 en Google · 22 valoraciones reales</span>
            </motion.div>

            <motion.h1
              variants={lineParent}
              initial="hidden"
              animate="visible"
              className="font-serif-display text-5xl sm:text-6xl md:text-7xl font-semibold tracking-tighter leading-[1.02] text-[#0A1F10]"
            >
              {LINES.map((line, i) => (
                <span key={i} className="block overflow-hidden py-[0.05em]">
                  <motion.span variants={lineChild} className="block">
                    {i === 2 ? (
                      <span className="text-[#5AB82D]">{line}</span>
                    ) : (
                      line
                    )}
                  </motion.span>
                </span>
              ))}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7 }}
              className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-[#4B5E51]"
            >
              Solucionamos desde la avería más compleja de humedades hasta instalaciones
              completas de fontanería. Puntualidad, honestidad y diagnóstico certero.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.85 }}
              className="mt-8 flex flex-col sm:flex-row gap-3"
            >
              <motion.a
                href={CONTACT.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-btn"
                whileTap={{ scale: 0.96 }}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#5AB82D] px-7 py-4 text-base font-bold text-white shadow-[0_12px_30px_rgba(90,184,45,0.35)] hover:bg-[#4fa526] transition-colors"
              >
                <WhatsAppIcon className="w-5 h-5" />
                Pedir presupuesto
              </motion.a>
              <motion.a
                href={`tel:${CONTACT.phoneRaw}`}
                data-testid="hero-call-btn"
                whileTap={{ scale: 0.96 }}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-[#0A1F10]/15 bg-white px-7 py-4 text-base font-bold text-[#0A1F10] hover:border-[#5AB82D] transition-colors"
              >
                <Phone className="w-5 h-5" />
                Llamar 722 29 49 23
              </motion.a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 1 }}
              className="mt-6 flex items-center gap-2 text-sm text-[#4B5E51]"
            >
              <MapPin className="w-4 h-4 text-[#5AB82D]" />
              Huelva capital y municipios cercanos
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="relative overflow-hidden rounded-[2px] aspect-square md:aspect-[5/4] bg-[#EBF6E4] shadow-[0_30px_80px_-20px_rgba(10,31,16,0.4)]">
                <motion.img
                  src={IMAGES.hero}
                  alt="Fontanero instalando grifería en Huelva"
                  style={{ y: imgY, scale: imgScale }}
                  className="absolute inset-0 w-full h-[118%] object-cover object-[50%_55%]"
                />
              </div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 1.1 }}
                className="absolute -bottom-6 -left-4 md:-left-8 bg-white rounded-sm px-5 py-4 shadow-[0_20px_50px_-15px_rgba(10,31,16,0.35)] border border-black/5"
              >
                <div className="font-serif-display text-3xl font-semibold text-[#5AB82D] leading-none">+20</div>
                <div className="text-xs font-semibold text-[#4B5E51] mt-1 uppercase tracking-wide">Años de experiencia</div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
